import React, { useEffect, useState } from "react";

// Reemplazo de <input type="month">.
// Safari de escritorio (Mac) no soporta type="month": lo muestra como un
// campo de texto libre. Donde el navegador sí lo soporta (Chrome, Edge,
// Safari de iPhone) se usa el input nativo; si no, se muestran dos selectores
// (mes y año). En ambos casos onChange recibe e.target.value = "YYYY-MM",
// igual que el input nativo, así que es un reemplazo directo.

const soportaMonth = (() => {
    if (typeof document === "undefined") return true;
    const input = document.createElement("input");
    input.setAttribute("type", "month");
    return input.type === "month";
})();

const MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const separar = (valor) => {
    const m = /^(\d{4})-(\d{2})/.exec(valor || "");
    return m ? { anio: m[1], mes: m[2] } : { anio: "", mes: "" };
};

const SelectoresMes = ({
    value,
    defaultValue,
    onChange,
    onKeyDown,
    disabled,
    required,
    id,
    name,
    min,
    max,
    className = "",
    style,
}) => {
    const controlado = value !== undefined;
    const [partes, setPartes] = useState(() => separar(controlado ? value : defaultValue));

    // Sincronizar cuando el padre cambia el valor. Si el padre deja "" mientras
    // el usuario eligió solo mes o solo año, se conserva esa selección parcial.
    useEffect(() => {
        if (!controlado) return;
        setPartes(prev => {
            if (value) return separar(value);
            return prev.anio && prev.mes ? { anio: "", mes: "" } : prev;
        });
    }, [value, controlado]);

    const emitir = (valor) => {
        if (!onChange) return;
        const target = { value: valor, name, id };
        onChange({ target, currentTarget: target, preventDefault() { }, stopPropagation() { } });
    };

    const cambiar = (campo, v) => {
        const nuevo = { ...partes, [campo]: v };
        setPartes(nuevo);
        const completo = nuevo.anio && nuevo.mes;
        if (completo) emitir(`${nuevo.anio}-${nuevo.mes}`);
        else if (partes.anio && partes.mes) emitir("");
    };

    const { anio: minAnio, mes: minMes } = separar(min);
    const { anio: maxAnio, mes: maxMes } = separar(max);
    const actual = new Date().getFullYear();
    const desde = Math.min(minAnio ? Number(minAnio) : 2020, partes.anio ? Number(partes.anio) : actual);
    const hasta = Math.max(maxAnio ? Number(maxAnio) : actual + 2, partes.anio ? Number(partes.anio) : actual);
    const anios = [];
    for (let a = hasta; a >= desde; a--) anios.push(String(a));

    const mesFueraDeRango = (mm) =>
        (minAnio && partes.anio === minAnio && mm < minMes) ||
        (maxAnio && partes.anio === maxAnio && mm > maxMes);

    const chico = /form-control-sm/.test(className);
    const claseSelect = `form-select${chico ? " form-select-sm" : ""}`;
    const claseContenedor = className.replace(/\bform-control(-sm|-lg)?\b/g, "").trim();

    return (
        <div className={`d-flex gap-1 ${claseContenedor}`} style={style}>
            <select
                id={id}
                className={claseSelect}
                value={partes.mes}
                onChange={e => cambiar("mes", e.target.value)}
                onKeyDown={onKeyDown}
                disabled={disabled}
                required={required}
                aria-label="Mes"
            >
                <option value="">Mes</option>
                {MESES.map((nombre, i) => {
                    const mm = String(i + 1).padStart(2, "0");
                    return (
                        <option key={mm} value={mm} disabled={mesFueraDeRango(mm)}>{nombre}</option>
                    );
                })}
            </select>
            <select
                className={claseSelect}
                style={{ maxWidth: "6.5rem" }}
                value={partes.anio}
                onChange={e => cambiar("anio", e.target.value)}
                onKeyDown={onKeyDown}
                disabled={disabled}
                required={required}
                aria-label="Año"
            >
                <option value="">Año</option>
                {anios.map(a => (
                    <option key={a} value={a}>{a}</option>
                ))}
            </select>
            {name && (
                <input type="hidden" name={name} value={partes.anio && partes.mes ? `${partes.anio}-${partes.mes}` : ""} />
            )}
        </div>
    );
};

const MonthInput = (props) => {
    if (soportaMonth) {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <input type="month" {...props} />;
    }
    return <SelectoresMes {...props} />;
};

export default MonthInput;
